const URGENCY_PRIORITY = {
    high: 3,
    medium: 2,
    low: 1,
};

const DEFAULT_HEADLINE = {
    title: 'Teacher Home',
    subtitle: 'Your next moves across active courses.',
    focus: 'Keep projects moving, stay on grade cadence, and reduce tomorrow-morning decisions.',
};

const DEFAULT_OPERATING_BRIEF = {
    primaryCourseId: null,
    label: 'Your next step',
    safeUseLabel: 'Teacher-facing support only',
};

const WARNING_MESSAGES = {
    MISSING_PROFILE: 'Teacher profile is incomplete.',
    MISSING_OPERATING_DATA: 'No teacher-home course data is available.',
    USING_FALLBACK_OPERATING_DATA: 'Showing fallback teacher-home data.',
    INCOMPLETE_PROFILE_COURSES: 'Teacher profile course data is incomplete; preserving fallback course context.',
    BACKEND_OPERATING_STATE_WARNING: 'Backend operating-state read model returned warnings.',
    LESSON_READINESS_WARNING: 'Lesson readiness returned warnings.',
};

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value == null) return [];
    return [value];
};

const hasKeys = (value) => (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
);

const cleanString = (value) => (
    typeof value === 'string' && value.trim() ? value.trim() : null
);

const normalizePrepAction = (action = {}, fallbackId) => {
    if (!action || typeof action !== 'object' || Array.isArray(action)) return null;

    const label = cleanString(action.label) || cleanString(action.title);
    if (!label) return null;

    return {
        id: cleanString(action.id) || fallbackId,
        label,
        detail: cleanString(action.detail) || cleanString(action.description),
        type: cleanString(action.type) || 'support',
        tone: cleanString(action.tone) || 'secondary',
    };
};

const buildFallbackPrepActions = (course = {}, courseId) => {
    const name = cleanString(course.name) || cleanString(course.course_name) || cleanString(course.subject) || 'this class';

    return [
        {
            id: `${courseId}-generate-lesson`,
            label: 'Generate tomorrow\'s lesson',
            detail: `Prepare the next lesson for ${name}.`,
            type: 'lesson',
            tone: 'primary',
        },
        {
            id: `${courseId}-review-pacing`,
            label: 'Review pacing',
            detail: `Check what ${name} needs next before class.`,
            type: 'pacing',
            tone: 'secondary',
        },
    ];
};

const slugify = (value) => (
    cleanString(value)?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || null
);

const dedupeById = (items = []) => {
    const seen = new Set();

    return items.filter((item) => {
        if (!item?.id || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
    });
};

const getCourseIdentityKeys = (course = {}) => (
    [
        cleanString(course.id),
        cleanString(course.name),
    ]
        .flatMap((value) => [value?.toLowerCase(), slugify(value)])
        .filter(Boolean)
);

const buildCourseIdentitySet = (courses = []) => (
    new Set(courses.flatMap(getCourseIdentityKeys))
);

const courseSignalMatches = (course, courseKeys) => (
    getCourseIdentityKeys(course).some((key) => courseKeys.has(key))
);

const courseLabelMatches = (courseLabel, courseKeys) => {
    const label = cleanString(courseLabel);
    if (!label) return false;

    return courseKeys.has(label.toLowerCase()) || courseKeys.has(slugify(label));
};

export const getGradeCadenceGaps = (gradeCadence = {}) => ({
    minorRemaining: Math.max(
        Number(gradeCadence.minorRequired || 0) - Number(gradeCadence.minorCompleted || 0),
        0
    ),
    majorRemaining: Math.max(
        Number(gradeCadence.majorRequired || 0) - Number(gradeCadence.majorCompleted || 0),
        0
    ),
});

export const getCoursePriorityScore = (course = {}) => {
    const { minorRemaining, majorRemaining } = getGradeCadenceGaps(course.gradeCadence);
    return ((URGENCY_PRIORITY[course.urgency] || 0) * 10) + (majorRemaining * 4) + minorRemaining;
};

export const getPrimaryOperatingCourse = (courses = [], primaryCourseId) => {
    const explicitCourse = courses.find((course) => course.id === primaryCourseId);
    if (explicitCourse) return explicitCourse;

    return [...courses].sort((a, b) => getCoursePriorityScore(b) - getCoursePriorityScore(a))[0] || null;
};

export const normalizeCalendarEvent = (event) => {
    if (!event || typeof event !== 'object') return null;

    return {
        time: cleanString(event.time) || cleanString(event.start_time) || cleanString(event.schedule) || 'All day',
        course: cleanString(event.course) || cleanString(event.subject) || cleanString(event.class_name) || 'Lesson',
        topic:
            cleanString(event.lesson_title) ||
            cleanString(event.topic) ||
            cleanString(event.title) ||
            cleanString(event.name) ||
            'Planned instruction',
        type: cleanString(event.type) || cleanString(event.category),
        gradeOpportunity: cleanString(event.gradeOpportunity) || cleanString(event.grade_opportunity),
        teacherPrep: cleanString(event.teacherPrep) || cleanString(event.teacher_prep),
        safetyLabel: cleanString(event.safetyLabel) || cleanString(event.safety_label),
        raw: event.raw || event,
    };
};

export const normalizeCalendarEvents = (calendarEvents = {}) => {
    if (!hasKeys(calendarEvents)) return {};

    return Object.entries(calendarEvents).reduce((eventsByDate, [dateKey, events]) => {
        const normalizedEvents = asArray(events)
            .map(normalizeCalendarEvent)
            .filter(Boolean);

        if (normalizedEvents.length > 0) {
            eventsByDate[dateKey] = normalizedEvents;
        }

        return eventsByDate;
    }, {});
};

export const normalizeFallbackCalendarEvents = (calendarEvents = {}) => {
    if (!hasKeys(calendarEvents)) return {};

    return Object.entries(calendarEvents).reduce((eventsByDay, [dayKey, events]) => {
        const normalizedEvents = asArray(events)
            .map(normalizeCalendarEvent)
            .filter(Boolean);

        if (normalizedEvents.length > 0) {
            eventsByDay[dayKey] = normalizedEvents;
        }

        return eventsByDay;
    }, {});
};

const normalizeCourse = (course = {}, index) => {
    const name =
        cleanString(course.name) ||
        cleanString(course.course_name) ||
        cleanString(course.subject) ||
        `Course ${index + 1}`;
    const id =
        cleanString(course.id) ||
        cleanString(course.course_id) ||
        slugify(name) ||
        `course-${index + 1}`;
    const configuredPrepActions = asArray(course.prepActions || course.prep_actions)
        .map((action, actionIndex) => normalizePrepAction(action, `${id}-prep-${actionIndex + 1}`))
        .filter(Boolean);

    return {
        ...course,
        id,
        name,
        shortName: cleanString(course.shortName) || cleanString(course.short_name) || name.slice(0, 3).toUpperCase(),
        urgency: cleanString(course.urgency) || 'low',
        nextActions: asArray(course.nextActions || course.next_actions).filter(Boolean),
        prepActions: configuredPrepActions.length > 0
            ? configuredPrepActions
            : buildFallbackPrepActions(course, id),
        teacherPrepChecklist: asArray(course.teacherPrepChecklist || course.teacher_prep_checklist).filter(Boolean),
        gradeCadence: {
            windowLabel: course.gradeCadence?.windowLabel || course.grade_cadence?.window_label || 'Current grade window',
            minorCompleted: Number(course.gradeCadence?.minorCompleted || course.grade_cadence?.minor_completed || 0),
            minorRequired: Number(course.gradeCadence?.minorRequired || course.grade_cadence?.minor_required || 0),
            majorCompleted: Number(course.gradeCadence?.majorCompleted || course.grade_cadence?.major_completed || 0),
            majorRequired: Number(course.gradeCadence?.majorRequired || course.grade_cadence?.major_required || 0),
        },
    };
};

const getProfileCourseGrade = (course = {}, userProfile = {}) => (
    cleanString(course.gradeLevel) ||
    cleanString(course.grade_level) ||
    cleanString(course.grade) ||
    cleanString(userProfile.gradeLevel) ||
    cleanString(userProfile.grade_level) ||
    cleanString(userProfile.grade)
);

const getProfileCoursePeriod = (course = {}) => (
    cleanString(course.period) ||
    cleanString(course.section) ||
    cleanString(course.block)
);

const buildProfileCourseCandidate = (course, index, userProfile = {}) => {
    const rawCourse = typeof course === 'string' ? { subject: course } : course;
    if (!rawCourse || typeof rawCourse !== 'object' || Array.isArray(rawCourse)) return null;

    const name =
        cleanString(rawCourse.name) ||
        cleanString(rawCourse.course_name) ||
        cleanString(rawCourse.subject) ||
        cleanString(rawCourse.title) ||
        cleanString(rawCourse.class_name);

    if (!name) return null;

    const grade = getProfileCourseGrade(rawCourse, userProfile);
    const period = getProfileCoursePeriod(rawCourse);
    const detailParts = [period && `Period ${period}`, grade && `Grade ${grade}`].filter(Boolean);
    const profileDetail = detailParts.length > 0 ? detailParts.join(' - ') : 'Teacher profile course';
    const isPrimary = rawCourse.is_primary === true || rawCourse.primary === true;

    return normalizeCourse({
        id: cleanString(rawCourse.id) || cleanString(rawCourse.course_id) || slugify(name),
        name,
        gradeLevel: grade,
        grade_level: grade,
        period,
        is_primary: rawCourse.is_primary === true,
        primary: rawCourse.primary === true,
        currentUnit:
            cleanString(rawCourse.currentUnit) ||
            cleanString(rawCourse.current_unit) ||
            cleanString(rawCourse.unit) ||
            profileDetail,
        status:
            cleanString(rawCourse.status) ||
            cleanString(rawCourse.pacing_status) ||
            'Loaded from teacher profile',
        urgency: cleanString(rawCourse.urgency) || (isPrimary ? 'medium' : 'low'),
        priorityReason:
            cleanString(rawCourse.priorityReason) ||
            cleanString(rawCourse.priority_reason) ||
            'Course signal came from the teacher profile, not student records.',
        bestNextMove:
            cleanString(rawCourse.bestNextMove) ||
            cleanString(rawCourse.best_next_move) ||
            `Prepare the next teacher-facing lesson move for ${name}.`,
        whyItMatters:
            cleanString(rawCourse.whyItMatters) ||
            cleanString(rawCourse.why_it_matters) ||
            'Using teacher-owned course context keeps the home surface aligned to the actual class list while avoiding student-sensitive data.',
        nextActions: asArray(rawCourse.nextActions || rawCourse.next_actions).length > 0
            ? rawCourse.nextActions || rawCourse.next_actions
            : [
                `Confirm the next lesson objective for ${name}.`,
                'Choose the next safe grade evidence opportunity.',
                'Prepare teacher-facing materials only.',
            ],
        teacherPrepChecklist: asArray(rawCourse.teacherPrepChecklist || rawCourse.teacher_prep_checklist).length > 0
            ? rawCourse.teacherPrepChecklist || rawCourse.teacher_prep_checklist
            : [
                `Review ${name} pacing before planning.`,
                'Avoid adding student-sensitive details to this operating state.',
            ],
        suggestedSaigeAction:
            cleanString(rawCourse.suggestedSaigeAction) ||
            cleanString(rawCourse.suggested_saige_action) ||
            `Plan ${name} lesson`,
        source: 'profile',
    }, index);
};

const getProfileCourseSignals = (userProfile = {}) => {
    const explicitCourses = asArray(userProfile?.courses)
        .map((course, index) => buildProfileCourseCandidate(course, index, userProfile))
        .filter(Boolean);

    if (explicitCourses.length > 0) {
        return dedupeById(explicitCourses);
    }

    const explicitCourseIds = new Set(explicitCourses.map((course) => course.id));
    const subjectCourses = asArray(userProfile?.subjects)
        .map((subject, index) => buildProfileCourseCandidate(subject, explicitCourses.length + index, userProfile))
        .filter((course) => course && !explicitCourseIds.has(course.id));

    return dedupeById([...explicitCourses, ...subjectCourses]);
};

const hasIncompleteCourseSignal = (userProfile = {}) => (
    asArray(userProfile?.courses).some((course) => {
        if (cleanString(course)) return false;
        if (!course || typeof course !== 'object' || Array.isArray(course)) return true;

        return !(
            cleanString(course.name) ||
            cleanString(course.course_name) ||
            cleanString(course.subject) ||
            cleanString(course.title) ||
            cleanString(course.class_name)
        );
    })
);

const mergeCourseSignals = (fallbackCourses = [], profileCourses = []) => {
    if (profileCourses.length === 0) return fallbackCourses;
    if (fallbackCourses.length === 0) return profileCourses;

    const fallbackCourseKeys = fallbackCourses.map((fallbackCourse) => ({
        course: fallbackCourse,
        keys: buildCourseIdentitySet([fallbackCourse]),
    }));

    return profileCourses.map((profileCourse) => {
        const fallbackMatch = fallbackCourseKeys.find(({ keys }) => (
            courseSignalMatches(profileCourse, keys)
        ))?.course;

        if (fallbackMatch) {
            return {
                ...profileCourse,
                ...fallbackMatch,
                id: fallbackMatch.id,
                source: 'hybrid',
                profileSignal: profileCourse,
            };
        }

        return profileCourse;
    });
};

const isGenericPreparationQueueItem = (item = {}) => {
    const courseLabel = cleanString(item.course)?.toLowerCase();
    return courseLabel === 'all active courses' || courseLabel === 'all courses';
};

const filterPreparationQueue = (items = [], courses = [], profileCourses = []) => {
    const queueItems = asArray(items);
    if (profileCourses.length === 0) return queueItems;

    const courseKeys = buildCourseIdentitySet(courses);
    if (courseKeys.size === 0) return [];

    return queueItems.filter((item) => (
        isGenericPreparationQueueItem(item) ||
        courseLabelMatches(item.course, courseKeys)
    ));
};

const filterFallbackCalendarEvents = (eventsByDay = {}, courses = [], profileCourses = []) => {
    if (profileCourses.length === 0) return eventsByDay;

    const courseKeys = buildCourseIdentitySet(courses);
    if (courseKeys.size === 0) return {};

    return Object.entries(eventsByDay).reduce((filteredEvents, [dayKey, events]) => {
        const matchingEvents = asArray(events).filter((event) => (
            courseLabelMatches(event.course, courseKeys)
        ));

        if (matchingEvents.length > 0) {
            filteredEvents[dayKey] = matchingEvents;
        }

        return filteredEvents;
    }, {});
};

const addWarning = (warnings, code) => {
    if (!warnings.some((warning) => warning.code === code)) {
        warnings.push({ code, message: WARNING_MESSAGES[code] || code });
    }
};

const hasProfileSignal = (userProfile) => Boolean(
    cleanString(userProfile?.name) ||
    cleanString(userProfile?.email) ||
    asArray(userProfile?.subjects).length > 0 ||
    asArray(userProfile?.courses).length > 0 ||
    cleanString(userProfile?.grade_level) ||
    cleanString(userProfile?.gradeLevel)
);

const hasReadinessSignal = (lessonReadiness) => Boolean(
    lessonReadiness &&
    typeof lessonReadiness === 'object' &&
    Object.keys(lessonReadiness).length > 0
);

const hasSavedDocumentSignal = (savedDocuments) => asArray(savedDocuments).length > 0;

const hasBackendOperatingSignal = (backendOperatingState) => Boolean(
    backendOperatingState &&
    typeof backendOperatingState === 'object' &&
    (
        asArray(backendOperatingState.courses).length > 0 ||
        asArray(backendOperatingState.prepareTomorrowQueue).length > 0 ||
        asArray(backendOperatingState.gradeCadenceWarnings).length > 0 ||
        backendOperatingState.readModelVersion
    )
);

const getSource = ({ fallbackData, userProfile, calendarEvents, lessonReadiness, savedDocuments, backendOperatingState }) => {
    const hasFallbackData = hasKeys(fallbackData);
    const hasLiveSignal =
        hasBackendOperatingSignal(backendOperatingState) ||
        hasProfileSignal(userProfile) ||
        hasKeys(calendarEvents) ||
        hasReadinessSignal(lessonReadiness) ||
        hasSavedDocumentSignal(savedDocuments);

    if (hasBackendOperatingSignal(backendOperatingState)) return hasFallbackData ? 'backend_hybrid' : 'backend';
    if (hasFallbackData && hasLiveSignal) return 'hybrid';
    if (hasFallbackData) return 'fallback';
    if (hasLiveSignal) return 'live';
    return 'fallback';
};

const normalizeBackendPrepQueue = (items = []) => (
    asArray(items)
        .map((item, index) => {
            if (!item || typeof item !== 'object') return null;
            const label = cleanString(item.label) || cleanString(item.title);
            if (!label) return null;

            return {
                id: cleanString(item.id) || `backend-prep-${index + 1}`,
                course: cleanString(item.course) || cleanString(item.courseName) || 'All active courses',
                label,
                output: cleanString(item.output) || cleanString(item.detail) || cleanString(item.description) || 'Prepare a teacher-reviewed draft.',
                reason: cleanString(item.reason) || 'Backend operating-state read model recommended this teacher-facing prep.',
                safetyLabel: cleanString(item.safetyLabel) || cleanString(item.safety_label) || 'Draft only; teacher approval required',
            };
        })
        .filter(Boolean)
);

const getBackendWeeklyPriorities = (backendOperatingState = {}) => (
    asArray(
        backendOperatingState.weeklyPlanningReview?.weeklyPriorities ||
        backendOperatingState.weeklyPriorities
    )
);

const normalizeBackendOperatingStateInput = (backendOperatingState) => (
    backendOperatingState && typeof backendOperatingState === 'object'
        ? backendOperatingState
        : {}
);

export function buildTeacherOperatingState({
    userProfile,
    calendarEvents,
    lessonReadiness,
    savedDocuments,
    fallbackData,
    fallbackCalendarEvents,
    backendOperatingState,
} = {}) {
    const backendState = normalizeBackendOperatingStateInput(backendOperatingState);
    const fallbackCourses = asArray(fallbackData?.courses).map(normalizeCourse);
    const backendCourses = asArray(backendState.courses).map(normalizeCourse);
    const profileCourses = backendCourses.length > 0 ? [] : getProfileCourseSignals(userProfile);
    const courses = backendCourses.length > 0
        ? backendCourses
        : mergeCourseSignals(fallbackCourses, profileCourses);
    const normalizedFallbackCalendarEvents = normalizeFallbackCalendarEvents(fallbackCalendarEvents);
    const source = getSource({
        fallbackData,
        userProfile,
        calendarEvents,
        lessonReadiness,
        savedDocuments,
        backendOperatingState: backendState,
    });
    const warnings = [];

    if (!hasProfileSignal(userProfile)) {
        addWarning(warnings, 'MISSING_PROFILE');
    }

    if (courses.length === 0) {
        addWarning(warnings, 'MISSING_OPERATING_DATA');
    }

    if (hasIncompleteCourseSignal(userProfile) && fallbackCourses.length > 0) {
        addWarning(warnings, 'INCOMPLETE_PROFILE_COURSES');
    }

    if (source === 'fallback') {
        addWarning(warnings, 'USING_FALLBACK_OPERATING_DATA');
    }

    if (asArray(lessonReadiness?.warnings).length > 0) {
        addWarning(warnings, 'LESSON_READINESS_WARNING');
    }

    if (asArray(backendState.warnings).length > 0) {
        addWarning(warnings, 'BACKEND_OPERATING_STATE_WARNING');
    }

    const profilePrimaryCourse = profileCourses.find((course) => course.is_primary === true || course.primary === true);
    const explicitPrimaryCourseId =
        backendState.dailyOperatingBrief?.primaryCourseId ||
        profilePrimaryCourse?.id ||
        fallbackData?.operatingBrief?.primaryCourseId ||
        null;
    const primaryCourse = getPrimaryOperatingCourse(courses, explicitPrimaryCourseId);
    const operatingBrief = {
        ...DEFAULT_OPERATING_BRIEF,
        ...(fallbackData?.operatingBrief || {}),
        ...(backendState.dailyOperatingBrief || {}),
        primaryCourseId: primaryCourse?.id || explicitPrimaryCourseId,
    };
    const backendPrepQueue = normalizeBackendPrepQueue(backendState.prepareTomorrowQueue);

    return {
        headline: {
            ...DEFAULT_HEADLINE,
            ...(fallbackData?.headline || {}),
        },
        generatedAt: new Date().toISOString(),
        source,
        operatingBrief,
        courses,
        profileCourseSignals: profileCourses,
        backendReadModel: hasBackendOperatingSignal(backendState) ? backendState : null,
        privacyBoundary: backendState.privacyBoundary || null,
        gradeCadenceWarnings: asArray(backendState.gradeCadenceWarnings),
        missingContextPrompts: asArray(backendState.missingContextPrompts),
        weeklyPriorities: getBackendWeeklyPriorities(backendState).length > 0
            ? getBackendWeeklyPriorities(backendState)
            : asArray(fallbackData?.weeklyPriorities),
        aiPreparationQueue: backendPrepQueue.length > 0
            ? backendPrepQueue
            : filterPreparationQueue(fallbackData?.aiPreparationQueue, courses, profileCourses),
        calendarEvents: normalizeCalendarEvents(calendarEvents),
        fallbackCalendarEvents: filterFallbackCalendarEvents(
            normalizedFallbackCalendarEvents,
            courses,
            profileCourses
        ),
        warnings,
    };
}
